// DESCRIPTION: Setup wizard orchestrator — holds step state, drives the
// progress bar, persists each step via REST, then triggers finalize.

import { useState, useCallback } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import apiFetch from '@wordpress/api-fetch';

import WelcomeStep from './steps/WelcomeStep';
import BusinessTypeStep from './steps/BusinessTypeStep';
import IdentityStep from './steps/IdentityStep';
import WorkHoursStep from './steps/WorkHoursStep';
import NotificationsStep from './steps/NotificationsStep';
import PaymentsStep from './steps/PaymentsStep';
import FinalizingStep from './steps/FinalizingStep';
import DoneStep from './steps/DoneStep';

// Step indexes: 0 welcome, 1-5 numbered, 6 finalizing, 7 done.
const STEP_WELCOME = 0;
const STEP_PERSONA = 1;
const STEP_IDENTITY = 2;
const STEP_HOURS = 3;
const STEP_NOTIFY = 4;
const STEP_PAYMENTS = 5;
const STEP_FINALIZING = 6;
const STEP_DONE = 7;

const TOTAL_NUMBERED_STEPS = 5;

const defaultState = {
	persona: '',
	identity: {
		business_name: '',
		timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
		currency: 'USD',
		logo_id: 0,
	},
	availability: {
		mon: [ { start: '09:00', end: '17:00' } ],
		tue: [ { start: '09:00', end: '17:00' } ],
		wed: [ { start: '09:00', end: '17:00' } ],
		thu: [ { start: '09:00', end: '17:00' } ],
		fri: [ { start: '09:00', end: '17:00' } ],
		sat: [],
		sun: [],
	},
	notifications: {
		admin_notify: true,
		customer_confirm: true,
		reminder: false,
	},
	payments: {
		method: 'offline',
		stripe_publishable_key: '',
		stripe_secret_key: '',
	},
};

/**
 * Send a step payload to the server.
 *
 * @since PBOOKING_SINCE
 *
 * @param {string} step Step key (matches PHP switch).
 * @param {Object} data Step data.
 *
 * @return {Promise}
 */
function saveStep( step, data ) {
	return apiFetch( {
		path: '/nemtly-booking/v1/setup/save',
		method: 'POST',
		data: { step, data },
	} );
}

/**
 * Skip the entire wizard — best-effort: marks setup complete by hitting the
 * finalize endpoint with whatever persona has been chosen (defaults to other).
 *
 * @since PBOOKING_SINCE
 */
function skipEntirely() {
	apiFetch( {
		path: '/nemtly-booking/v1/setup/finalize',
		method: 'POST',
	} ).finally( () => {
		const config = window.nemtlyBookingSetup || {};
		window.location.href = `${
			config.adminUrl || '/wp-admin/'
		}admin.php?page=nemtly-booking`;
	} );
}

/**
 * Root component. Owns step + form state and routes between step components.
 *
 * @since PBOOKING_SINCE
 *
 * @return {JSX.Element}
 */
export default function SetupWizard() {
	const [ step, setStep ] = useState( STEP_WELCOME );
	const [ data, setData ] = useState( defaultState );
	const [ isLoading, setIsLoading ] = useState( false );
	const [ finalizeResult, setFinalizeResult ] = useState( null );

	const numberedIndex = Math.min( Math.max( step, 0 ), TOTAL_NUMBERED_STEPS );
	const progress = ( numberedIndex / TOTAL_NUMBERED_STEPS ) * 100;

	const advance = useCallback( () => setStep( ( s ) => s + 1 ), [] );
	const goBack = useCallback(
		() => setStep( ( s ) => Math.max( 0, s - 1 ) ),
		[]
	);

	const handleSaveAndNext = useCallback(
		async ( stepKey, stepData ) => {
			setIsLoading( true );
			try {
				await saveStep( stepKey, stepData );
				advance();
			} catch ( err ) {
				// eslint-disable-next-line no-alert
				alert(
					__( 'Could not save. Please try again.', 'nemtly-booking' )
				);
			} finally {
				setIsLoading( false );
			}
		},
		[ advance ]
	);

	const handleFinalizeDone = useCallback( ( result ) => {
		setFinalizeResult( result );
		setStep( STEP_DONE );
	}, [] );

	const handleFinalizeError = useCallback( () => {
		// eslint-disable-next-line no-alert
		alert(
			__(
				'Something went wrong finalizing setup. Please try again.',
				'nemtly-booking'
			)
		);
		setStep( STEP_PAYMENTS );
	}, [] );

	switch ( step ) {
		case STEP_WELCOME:
			return (
				<WelcomeStep onStart={ advance } onSkipAll={ skipEntirely } />
			);

		case STEP_PERSONA:
			return (
				<BusinessTypeStep
					progress={ progress }
					value={ data.persona }
					onChange={ ( persona ) =>
						setData( ( d ) => ( { ...d, persona } ) )
					}
					onBack={ goBack }
					onContinue={ () =>
						handleSaveAndNext( 'persona', {
							business_persona: data.persona,
						} )
					}
					isLoading={ isLoading }
				/>
			);

		case STEP_IDENTITY:
			return (
				<IdentityStep
					progress={ progress }
					value={ data.identity }
					onChange={ ( identity ) =>
						setData( ( d ) => ( { ...d, identity } ) )
					}
					onBack={ goBack }
					onSkip={ advance }
					onContinue={ () =>
						handleSaveAndNext( 'identity', data.identity )
					}
					isLoading={ isLoading }
				/>
			);

		case STEP_HOURS:
			return (
				<WorkHoursStep
					progress={ progress }
					value={ data.availability }
					onChange={ ( availability ) =>
						setData( ( d ) => ( { ...d, availability } ) )
					}
					onBack={ goBack }
					onSkip={ advance }
					onContinue={ () =>
						handleSaveAndNext( 'availability', data.availability )
					}
					isLoading={ isLoading }
				/>
			);

		case STEP_NOTIFY:
			return (
				<NotificationsStep
					progress={ progress }
					value={ data.notifications }
					onChange={ ( notifications ) =>
						setData( ( d ) => ( { ...d, notifications } ) )
					}
					onBack={ goBack }
					onSkip={ advance }
					onContinue={ () =>
						handleSaveAndNext( 'notifications', data.notifications )
					}
					isLoading={ isLoading }
				/>
			);

		case STEP_PAYMENTS:
			return (
				<PaymentsStep
					progress={ progress }
					value={ data.payments }
					onChange={ ( payments ) =>
						setData( ( d ) => ( { ...d, payments } ) )
					}
					onBack={ goBack }
					onSkip={ advance }
					onContinue={ () =>
						handleSaveAndNext( 'payments', data.payments )
					}
					isLoading={ isLoading }
				/>
			);

		case STEP_FINALIZING:
			return (
				<FinalizingStep
					onDone={ handleFinalizeDone }
					onError={ handleFinalizeError }
				/>
			);

		case STEP_DONE:
			return <DoneStep finalizeResult={ finalizeResult } />;

		default:
			return null;
	}
}
